// THE TOP OF THE HOME — the greeting's sub-line and the summary
// (DESIGN-WL-HOME-TOP-STRIP-SUMMARY-1; HOME_SIGNALS_CONTRACT v1 §2.6; Nika's
// pick 2026-09-28 row 2 "A or B": the Brief and the Signals both ship as a
// personal option).
//
// Everything here renders from ONE model — `el.call('homeSummaryCompose',
// root)` (functions/homeSummaryCompose.js): today the signals with
// deterministic sentences, later the AI source with the same shape. No
// component computes a number: every count on screen is a signal's own.
//
//   HomeSignalsLine   the greeting's sub-line — the top two signals joined by
//                     " · ", each a link to its list; "Nothing needs you right
//                     now" only when every installed provider answered empty;
//                     a skeleton line while they answer (no flash of nothing).
//                     ONE VOICE: it steps aside whenever the summary shows the
//                     signals (homeSummaryShowsSignals) — never the same fact
//                     twice on screen.
//   HomeTopSummary    the summary under it, in the person's style
//                     (functions/homeLook.js `summaryStyle`):
//     HomeSummaryBrief   two calm sentences, each fact a link inline in the
//                        sentence (a heavier weight, no space of its own) that
//                        opens its list, then "Updated · Refresh · Open as board";
//     HomeSummaryTiles   up to four compact signal tiles — the kind's glyph on
//                        a round plate (a severity dot on it), the count, the
//                        words; the calm "nothing" tile; their skeleton.
//
// A signal opens its list through `homeSignalsOpen` → the shell's
// `openInSlot` (slot vs page is its decision); a modified click is the
// browser's (a new tab).
//
// Quality law (HOME_AMBIENT_SKIN_SPEC §3/§3b): no text here is cut, clamped or
// ellipsised — words wrap between words, a tile is never narrower than its
// longest word.

// A signal as a link: its words in the title ink, the urgent ones in the
// danger ink (a static rule keyed on `data-severity` — the state reaches the
// CSS as an attribute, never a create-only case block).
export const HomeSignalLink = {
  extends: 'Link',
  class: 'home-signal-link',
  href: (el, s) => (s && s.href) || null,
  text: (el, s) => el.call('homeSignalsText', s),
  dataSeverity: (el, s) => (s && s.severity) || null,
  color: 'title',
  fontWeight: '500',
  textDecoration: 'none',
  round: 'X',
  cursor: 'pointer',
  transition: 'background-color A defaultBezier',
  '&[data-severity="urgent"]': { color: 'dangerInk' },
  ':hover': { textDecoration: 'underline' },
  ':active': { background: 'selected' },
  onClick: (e, el, s) => el.call('homeSignalsOpen', e, s)
}

// The Brief's fact — the same link, inline in the sentence in a heavier
// weight (the urgent one in the danger ink, HomeSignalLink's rule), underlined
// on hover. It adds NO space of its own: the sentence's spaces are the only
// spaces. It used to be a pill — an inline padding on both ends, cloned onto
// every line of a wrapped fact — and the served Brief read "… უმოძრაოდ .
// ასევე გაქვთ 1 ღია დავალება ." with a gap before each full stop and at the
// start of a wrapped line (qa 2026-09-29; its `layer` wash painted nothing:
// `layer` is a theme, not a colour).
export const HomeSignalChip = {
  extends: 'HomeSignalLink',
  class: 'home-signal-chip',
  display: 'inline',
  fontWeight: '700'
}

// THE SIGNALS STYLE — one compact tile per signal (Nika 2026-09-29: "keep
// todays default as default, add signals as option in personalize but make
// better design for it"). The first cut was a flat block with a big numeral,
// the urgent one in red text only. Now, left to right, on one line:
//
//   HomeSignalGlyph   the signal's KIND as a glyph (homeSignalIcon) on a
//                     round plate — the Dock's calm glyph plate, round; an
//                     urgent / attention signal tints it and carries a small
//                     dot on it: FILLED for urgent, a RING for attention, none
//                     for info — the severity reads without colour too;
//   Numeral           the count, large, in the title ink;
//   Words             the words without the count, muted, wrapping between
//                     words onto a second line — never cut.
//
// The surface is the skin's `card` (glass on the ambient skin, flat and
// borderless on the solid one; both schemes). The row keeps the Dock's rhythm:
// the same plate size and padding, so a one-line tile is exactly a Dock row
// tall. The whole tile is ONE link to its list (openInSlot; a modified click is
// the browser's); hover is an inset wash, focus-visible the house focus ring,
// and its name carries the severity in words (homeSignalsLabel).
export const HomeSignalTile = {
  extends: 'Link',
  class: 'home-signal-tile',
  href: (el, s) => (s && s.href) || null,
  aria: { label: (el, s) => el.call('homeSignalsLabel', s) },
  dataSeverity: (el, s) => (s && s.severity) || null,
  dataKind: (el, s) => (s && s.kind) || null,
  theme: 'card',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 'Z',
  // An item of the Signals row (HomeSummaryTiles TilesRow, a wrapping flex
  // row): it starts at the F step, grows to share its line, and is never
  // narrower than its longest word — a tile that does not fit moves to the next
  // line instead of lying over its neighbour. F, not E: at E two tiles shared a
  // 390 px line and a label wrapped to three lines; at F a phone stacks the
  // tiles (1-2 lines each) and 1440 / 2560 keep one row.
  width: 'F',
  flexGrow: '1',
  minWidth: 'min-content',
  padding: 'Y Z Y Y',
  round: 'radiusCard',
  color: 'title',
  fontWeight: '400',
  textDecoration: 'none',
  cursor: 'pointer',
  // the wash lies under the words (its own stacking context, the layer at -1)
  isolation: 'isolate',
  '::after': {
    content: '""',
    position: 'absolute',
    inset: '0',
    zIndex: '-1',
    borderRadius: 'inherit',
    background: 'cardHover',
    opacity: '0',
    pointerEvents: 'none',
    transition: 'opacity A defaultBezier'
  },
  ':hover::after': { opacity: '1' },
  ':active::after': { opacity: '1', background: 'cardPress' },
  // The house focus ring (the `focusRing` shadow: a document gap, then the
  // highlight) — the Link base only takes the outline away.
  ':focus-visible': { shadow: 'focusRing' },
  onClick: (e, el, s) => el.call('homeSignalsOpen', e, s),

  HomeSignalGlyph: {},
  Numeral: {
    tag: 'span',
    class: 'home-signal-numeral',
    flexShrink: '0',
    fontSize: 'C',
    fontWeight: '700',
    lineHeight: '1',
    fontVariantNumeric: 'tabular-nums',
    text: (el, s) => String((s && s.count) || '')
  },
  Words: {
    tag: 'span',
    class: 'home-signal-words',
    fontSize: 'Z',
    lineHeight: '1.35',
    color: 'caption',
    overflowWrap: 'normal',
    wordBreak: 'normal',
    text: (el, s) => el.call('homeSignalsText', s, { bare: true })
  }
}

// The signal's glyph plate: the Dock's calm glyph plate (AppGlyphTile — the
// `layer` wash, the title ink, the control size), round. An urgent or attention
// signal washes the plate in its hue (wash + neutral ink, DESIGN_LANGUAGE §8.5)
// and wears a small dot in its ink: FILLED for urgent, a RING for attention —
// the shape tells the two apart without colour; info has none.
export const HomeSignalGlyph = {
  extends: 'AppGlyphTile',
  class: 'home-signal-glyph',
  position: 'relative',
  round: 'radiusPill',
  ':is(.home-signal-tile[data-severity="urgent"]) &': { background: 'red.15' },
  ':is(.home-signal-tile[data-severity="attention"]) &': { background: 'yellow.15' },
  Icon: {
    name: (el, s) => el.call('homeSignalIcon', s),
    boxSize: 'iconMd'
  },
  Dot: {
    tag: 'span',
    class: 'home-signal-dot',
    ariaHidden: 'true',
    display: 'none',
    position: 'absolute',
    top: '0',
    right: '0',
    boxSize: 'Y',
    round: 'radiusPill',
    ':is(.home-signal-tile[data-severity="urgent"]) &': {
      display: 'block',
      background: 'dangerInk'
    },
    ':is(.home-signal-tile[data-severity="attention"]) &': {
      display: 'block',
      border: 'hairline2 solid warningInk'
    }
  }
}

// The Signals style with nothing to say: one calm tile in the row's place —
// "Nothing needs you right now" beside a check plate, or, when an installed
// app will not come, "Some apps couldn't load" beside an info plate. It is
// the row's own voice (the greeting's sub-line steps aside,
// homeSummaryShowsSignals); it is not a link — there is nothing to open.
export const HomeSignalNone = {
  class: 'home-signal-none',
  role: 'status',
  theme: 'card',
  display: 'flex',
  alignItems: 'center',
  gap: 'Z',
  width: 'F',
  flexGrow: '1',
  minWidth: 'min-content',
  padding: 'Y Z Y Y',
  round: 'radiusCard',
  color: 'title',
  AppGlyphTile: {
    round: 'radiusPill',
    Icon: {
      name: (el, s) =>
        (el.call('homeSummaryCompose', s.root) || {}).status === 'unavailable'
          ? 'sfInfoOval'
          : 'check',
      boxSize: 'iconMd'
    }
  },
  Words: {
    tag: 'span',
    fontSize: 'Z',
    lineHeight: '1.35',
    color: 'caption',
    overflowWrap: 'normal',
    wordBreak: 'normal',
    // The words only in their state: the hidden tile never carries "Nothing
    // needs you right now." for a page-text reader. With zero items while an
    // installed app will not come, the loader's own words.
    text: (el, s) => {
      const status = (el.call('homeSummaryCompose', s.root) || {}).status
      if (status === 'nothing') return el.call('polyglot', 'workspace.home.summary.none')
      if (status === 'unavailable') return el.call('polyglot', 'workspace.activation.partialTitle')
      return ''
    }
  }
}

// A tile's skeleton while the providers answer — the tile's own geometry (the
// card, the round plate, one line of words), so the answer lands in place.
export const HomeSignalSkeletonTile = {
  theme: 'card',
  display: 'flex',
  alignItems: 'center',
  gap: 'Z',
  width: 'F',
  flexGrow: '1',
  minWidth: 'min-content',
  padding: 'Y Z Y Y',
  round: 'radiusCard',
  SkeletonCircle: { boxSize: 'controlH', flexShrink: '0' },
  SkeletonRow: { width: 'D', maxWidth: '100%' }
}

// The greeting's sub-line (contract §2.6). The line and its skeleton share one
// line box, so the answer replaces the skeleton without moving the page.
//
// ONE VOICE (qa round 9, dev 2026-09-29): the line read "9 overdue next steps ·
// 2 deals idle 14+ days" and the Brief under it said the same two facts again
// (the Signals tiles did too). The summary is the place that says what needs
// the person, so the line steps aside while the summary shows the signals —
// its sentences, its tiles, or their skeleton — and speaks only when the
// summary shows none of them: "Nothing needs you right now".
export const HomeSignalsLine = {
  class: 'home-signals-line',
  hide: (el, s) => el.call('homeSummaryShowsSignals', s.root),
  // This line sits above the summary — the top sky's clear edge
  // (functions/ambientBackdrop.js). Showing it, hiding it or swapping its
  // skeleton for words moves the summary, so the edge re-measures here, after
  // the change is on the page (measured on dev, Vault 2560: the edge stayed at
  // the waiting-time 149 px while the summary settled at 181 px). Not a
  // ResizeObserver: it never fires in a hidden tab.
  stateDeps: [
    (el, s) => !!el.call('homeSummaryShowsSignals', s.root),
    (el, s) => (el.call('homeSummaryCompose', s.root) || {}).status || null
  ],
  onStateUpdate: (el) => el.call('ambientSkySync', el),
  display: 'flex',
  flexDirection: 'column',
  minWidth: '0',

  SignalsText: {
    tag: 'p',
    margin: '0',
    fontSize: 'A',
    lineHeight: '1.5',
    color: 'inactive',
    overflowWrap: 'normal',
    hide: (el, s) => (el.call('homeSummaryCompose', s.root) || {}).status === 'waiting',
    children: (el, s) => {
      const model = el.call('homeSummaryCompose', s.root) || {}
      if (model.status === 'nothing') {
        return [
          { key: 'none', tag: 'span', text: el.call('polyglot', 'workspace.home.summary.none') }
        ]
      }
      // Zero items, but an installed app will not come: never "nothing" —
      // the loader's own words for it.
      if (model.status === 'unavailable') {
        return [
          {
            key: 'unavailable',
            tag: 'span',
            text: el.call('polyglot', 'workspace.activation.partialTitle')
          }
        ]
      }
      const out = []
      const line = model.line || []
      line.forEach((sig, i) => {
        if (i) out.push({ key: 'dot' + i, tag: 'span', text: ' · ', ariaHidden: 'true' })
        out.push({ key: sig.id, extends: 'HomeSignalLink', state: { ...sig } })
      })
      return out
    }
  },
  SkeletonRow: {
    width: 'H',
    maxWidth: '60%',
    height: 'A',
    margin: 'Y 0',
    hide: (el, s) => (el.call('homeSummaryCompose', s.root) || {}).status !== 'waiting'
  }
}

// The summary, in the person's style. It holds a skeleton of its own shape
// while the providers answer. With nothing to say, the Signals style shows its
// calm "nothing" tile, and the Brief keeps only its Updated · Refresh row (the
// greeting's sub-line says "Nothing needs you right now" there).
export const HomeTopSummary = {
  tag: 'section',
  class: 'home-top-summary',
  'aria-label': (el) => el.call('polyglot', 'workspace.home.brief.label'),
  display: 'flex',
  flexDirection: 'column',
  minWidth: '0',
  // The ambient sky's clear edge is this section's top (app.js mounts the
  // sky with `fadeAnchor: ['HomeTopSummary', …]`): re-measure it once this has a
  // box (functions/ambientBackdrop.js ambientSkySync).
  onRender: (el) => el.call('ambientSkySync', el),

  HomeSummaryBrief: {
    hide: (el, s) => (el.call('homeLookRead', s.root) || {}).summaryStyle !== 'brief'
  },
  HomeSummaryTiles: {
    hide: (el, s) => (el.call('homeLookRead', s.root) || {}).summaryStyle !== 'signals'
  }
}

// Brief — two calm sentences (the model's runs: words, chips, a start time),
// then the micro-row.
export const HomeSummaryBrief = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'Y',
  minWidth: '0',
  // The AI source (functions/homeSummaryAi.js): asked once the signals settle,
  // again when they, the language or the style change.
  subscribeTo: ['_homeSignals', 'lang', '_homeLook'],
  onRender: (el, s) => el.call('homeSummaryAiEnsure', s.root),
  onUpdate: (el, s) => el.call('homeSummaryAiEnsure', s.root),

  BriefText: {
    tag: 'p',
    margin: '0',
    maxWidth: 'I',
    fontSize: 'A',
    lineHeight: '1.75',
    color: 'caption',
    overflowWrap: 'normal',
    hide: (el, s) => {
      const m = el.call('homeSummaryCompose', s.root) || {}
      return (m.briefStatus || m.status) !== 'ready'
    },
    children: (el, s) => {
      const out = []
      const brief = (el.call('homeSummaryCompose', s.root) || {}).brief || []
      brief.forEach((runs, i) => {
        if (i) out.push({ key: 'gap' + i, tag: 'span', text: ' ' })
        for (const run of runs) {
          if (run.chip)
            out.push({ key: run.key, extends: 'HomeSignalChip', state: { ...run.chip } })
          else if (run.time) out.push({ key: run.key, tag: 'span', color: 'title', text: run.time })
          else out.push({ key: run.key, tag: 'span', text: run.text })
        }
      })
      return out
    }
  },

  // The AI sentence's signals — their OWN row under the sentence (design-d2,
  // 2026-10-01: the deploy-11 sweep read them running on after the answer's
  // last full stop; the sentence stays alone as the paragraph). One chip per
  // signal, gap Z, wrapping chip by chip: a chip keeps its longest word whole
  // (a flex item's min-content) and breaks only between words. The template's
  // facts are words of its own sentences, so this row is the AI answer's only.
  BriefChips: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: 'Z',
    maxWidth: 'I',
    hide: (el, s) => {
      const m = el.call('homeSummaryCompose', s.root) || {}
      return (m.briefStatus || m.status) !== 'ready' || !(m.briefChips || []).length
    },
    children: (el, s) =>
      ((el.call('homeSummaryCompose', s.root) || {}).briefChips || []).map((sig) => ({
        key: 'chip-' + sig.id,
        extends: 'HomeSignalChip',
        state: { ...sig }
      }))
  },

  BriefSkeleton: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'Y',
    padding: 'Y 0',
    hide: (el, s) => {
      const m = el.call('homeSummaryCompose', s.root) || {}
      return (m.briefStatus || m.status) !== 'waiting'
    },
    SkeletonRow_1: { width: 'I', maxWidth: '100%', height: 'A' },
    SkeletonRow_2: { width: 'H', maxWidth: '80%', height: 'A' }
  },

  BriefMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 'Y Z',
    hide: (el, s) => (el.call('homeSummaryCompose', s.root) || {}).status === 'waiting',
    // "Updated 06:12" — the time the last answer landed.
    // Only a real time of THIS summary: with none, the label is hidden AND
    // empty — a hidden node never carries a formatted zero ("Updated 04:00
    // AM") for a page-text reader.
    AppMicroLabel: {
      // `caption` on the home stage (the DateLabel's reason, pages/main.js).
      color: 'caption',
      hide: (el, s) => !(el.call('homeSummaryCompose', s.root) || {}).updatedAt,
      text: (el, s) => {
        const at = (el.call('homeSummaryCompose', s.root) || {}).updatedAt
        if (!at) return ''
        return el.call('polyglot', 'workspace.home.brief.updated').replace(
          '{time}',
          el.call('localizedDate', at, { hour: '2-digit', minute: '2-digit' })
        )
      }
    },
    // The row WRAPS on a phone: at 390 px "Open as board" opens line 2. A
    // ghost button's label sits one inset (Z2) inside its box, so that line
    // began ~12 px right of the UPDATED label's edge. The board button hangs
    // its inset into the gutter (InlineSelect's optical pull: a host fact, so
    // a margin at the call site) and Refresh gives the same inset back after
    // itself. Both are the compact size (font Z), so the pull and the
    // give-back are one length: the one-line row (1440 / 2560) keeps every
    // distance and every wrap point. The label and Refresh always share line
    // 1 (even at 320 px), so only the board button opens a line.
    AppCapsuleBtnGhost: {
      compact: true,
      marginInlineEnd: 'Z2',
      text: '{{ common.action.refresh | polyglot }}',
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('homeSignalsInvalidate')
      }
    },
    AppCapsuleBtnGhost_board: {
      compact: true,
      marginInlineStart: '-Z2',
      hide: (el, s) => !el.call('assistantBoardPageOnHome', s.root),
      text: '{{ workspace.home.brief.openAsBoard | polyglot }}',
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('homeSummaryOpenAsBoard')
      }
    }
  }
}

// Signals — the top four signals as tiles in ONE wrapping row: each tile
// starts at the F step and grows to share its line, never narrower than its
// longest word; a tile that does not fit moves to the next line. It was a grid
// of E-wide tracks, and at 390 px (ka, qa 2026-09-29) the 188 px tile
// "9 ვადაგადაცილებული შემდგომი ნაბიჯი" lay 65 px under its neighbour — a
// track never grows for its item's words; a flex line does.
export const HomeSummaryTiles = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: '0',

  TilesRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'Z',
    hide: (el, s) => (el.call('homeSummaryCompose', s.root) || {}).status !== 'ready',
    childExtends: 'HomeSignalTile',
    childrenAs: 'state',
    children: (el, s) =>
      ((el.call('homeSummaryCompose', s.root) || {}).tiles || []).map((sig) => ({
        ...sig,
        key: sig.id
      }))
  },

  // Nothing needs the person: the row's own calm tile.
  NoneRow: {
    display: 'flex',
    hide: (el, s) => {
      const status = (el.call('homeSummaryCompose', s.root) || {}).status
      return status !== 'nothing' && status !== 'unavailable'
    },
    HomeSignalNone: {}
  },

  // The providers are answering: three tiles' geometry, wrapping as the row
  // does.
  TilesSkeleton: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'Z',
    hide: (el, s) => (el.call('homeSummaryCompose', s.root) || {}).status !== 'waiting',
    HomeSignalSkeletonTile_1: {},
    HomeSignalSkeletonTile_2: {},
    HomeSignalSkeletonTile_3: {}
  }
}
